import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, Target, Users, TrendingUp, Award, CheckCircle, Play, ChevronRight, Quote } from 'lucide-react';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (hasAnimated) return;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration * 60);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 1000 / 60);
        }
      },
      { threshold: 0.5 }
    );

    const element = document.getElementById(`counter-${end}`);
    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span id={`counter-${end}`}>{count}{suffix}</span>;
};

// Hero Section - More Dynamic
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section
      className="relative min-h-screen flex items-center bg-brand-charcoal overflow-hidden"
      data-testid="hero-section"
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(92,45,145,0.3),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(196,158,77,0.2),transparent_50%)]" />
        <motion.div 
          style={{ y }}
          className="absolute inset-0 bg-cover bg-center opacity-20"
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.5 }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-charcoal/50 to-brand-charcoal" />
        </motion.div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 right-1/4 w-64 h-64 bg-brand-purple/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ y: [0, 20, 0], rotate: [0, -5, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute bottom-1/4 left-1/4 w-96 h-96 bg-brand-gold/10 rounded-full blur-3xl"
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div style={{ opacity }}>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-8"
            >
              <Sparkles className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Transforming Leaders Since 2010</span>
            </motion.div>

            {/* Headline */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6"
            >
              Build the{' '}
              <span className="relative">
                <span className="text-gradient-gold">Competitive Edge</span>
                <motion.span
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute bottom-2 left-0 h-1 bg-brand-gold/40"
                />
              </span>{' '}
              Your Organization Needs
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-300 mb-10 leading-relaxed max-w-xl"
            >
              Strategic consulting, transformative training, and executive coaching 
              that empowers leaders to navigate complexity, inspire teams, and 
              drive extraordinary results.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-8 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
                data-testid="hero-cta-primary"
              >
                Start Your Transformation
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/luminarc"
                className="inline-flex items-center justify-center gap-2 border-2 border-brand-gold/50 text-brand-gold font-semibold px-8 py-4 hover:bg-brand-gold/10 hover:border-brand-gold transition-all duration-300 group"
                data-testid="hero-cta-secondary"
              >
                <Sparkles className="w-5 h-5" />
                Discover Luminarc™
              </Link>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-12 flex items-center gap-8"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full border-2 border-brand-charcoal bg-gradient-to-br from-brand-purple to-brand-purple-lite flex items-center justify-center text-white text-xs font-bold"
                  >
                    {['S', 'M', 'R', 'A', 'D'][i - 1]}
                  </div>
                ))}
              </div>
              <div className="text-sm">
                <p className="text-white font-semibold">500+ Leaders Transformed</p>
                <p className="text-gray-400">Join industry pioneers</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Stats Cards */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="hidden lg:grid grid-cols-2 gap-4"
          >
            {[
              { value: '95', suffix: '%', label: 'Client Satisfaction', color: 'text-brand-gold' },
              { value: '40', suffix: '%', label: 'Avg. Performance Gain', color: 'text-white' },
              { value: '15', suffix: '+', label: 'Years Experience', color: 'text-white' },
              { value: '200', suffix: '+', label: 'Organizations Served', color: 'text-brand-gold' },
            ].map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl blur-xl" />
                <div className="relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:bg-white/15 transition-all duration-300">
                  <p className={`text-4xl font-bold ${stat.color}`}>
                    <AnimatedCounter end={parseInt(stat.value)} suffix={stat.suffix} />
                  </p>
                  <p className="text-gray-300 text-sm mt-1">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-gray-500 text-xs uppercase tracking-widest">Scroll to explore</span>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-brand-gold rounded-full"
            />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Services Overview - Enhanced with correct order
const ServicesOverview = () => {
  const services = [
    {
      icon: Target,
      title: 'Consulting & Strategy',
      description: 'Navigate complexity with confidence. We help you identify opportunities, optimize operations, and build strategies that drive sustainable competitive advantage.',
      link: '/services/consulting',
      color: 'from-brand-purple to-brand-purple-mid',
      stats: '40% avg efficiency gain',
    },
    {
      icon: Users,
      title: 'Corporate Training',
      description: 'Transform your workforce with immersive learning experiences. Build critical capabilities that stick and create lasting behavioral change across your organization.',
      link: '/services/corporate-training',
      color: 'from-brand-gold to-brand-gold-lite',
      stats: '35% engagement increase',
    },
    {
      icon: TrendingUp,
      title: 'Coaching & Mentorship',
      description: 'Unlock individual potential with personalized executive coaching. Help your leaders find their authentic voice and develop capabilities to excel at the next level.',
      link: '/services/coaching',
      color: 'from-brand-purple-lite to-brand-purple',
      stats: '85% report promotion',
    },
  ];

  return (
    <section className="py-24 bg-brand-bg-light relative overflow-hidden" data-testid="services-overview">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-brand-lavender/50 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block bg-brand-purple/10 text-brand-purple text-sm font-semibold px-4 py-2 rounded-full mb-4">
            Our Services
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-6">
            Comprehensive Solutions for{' '}
            <span className="text-brand-purple">Growth</span>
          </h2>
          <p className="text-brand-muted text-lg max-w-2xl mx-auto">
            From strategy to execution, we partner with you at every stage of your transformation journey.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
            >
              <Link
                to={service.link}
                className="group block bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-500 h-full border border-transparent hover:border-brand-purple/20 relative overflow-hidden"
                data-testid={`service-card-${index}`}
              >
                {/* Hover gradient overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`} />
                
                <div className="relative">
                  {/* Icon with gradient background */}
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300`}>
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  
                  <h3 className="font-heading text-2xl font-bold text-brand-charcoal mb-3 group-hover:text-brand-purple transition-colors">
                    {service.title}
                  </h3>
                  
                  <p className="text-brand-muted leading-relaxed mb-6">
                    {service.description}
                  </p>
                  
                  {/* Stats badge */}
                  <div className="inline-block bg-brand-lavender px-3 py-1 rounded-full mb-4">
                    <span className="text-brand-purple text-sm font-semibold">{service.stats}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-brand-purple font-semibold">
                    <span>Learn More</span>
                    <ChevronRight className="w-5 h-5 transform group-hover:translate-x-2 transition-transform duration-300" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Luminarc Section - Premium & Mysterious
const LuminarcSection = () => {
  return (
    <section className="relative py-32 bg-brand-charcoal overflow-hidden" data-testid="luminarc-section">
      {/* Animated gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(196,158,77,0.15),transparent_70%)]" />
        <motion.div
          animate={{ 
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%239C92AC" fill-opacity="0.1"%3E%3Cpath d="m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            {/* Exclusive badge */}
            <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/30 rounded-full px-4 py-2 mb-6">
              <Sparkles className="w-4 h-4 text-brand-gold" />
              <span className="text-brand-gold text-sm font-medium">Flagship Experience</span>
            </div>
            
            <h2 className="font-heading text-5xl sm:text-6xl font-bold text-white mb-6">
              Luminarc<span className="text-brand-gold">™</span>
            </h2>
            
            <p className="text-2xl text-brand-gold-lite font-light italic mb-8 leading-relaxed">
              "Where logic ends, perspective begins."
            </p>
            
            <p className="text-gray-300 text-lg leading-relaxed mb-8">
              A transformational, hands-on experience designed to develop{' '}
              <span className="text-white font-semibold">narrative thinking</span> in the age of AI. 
              Not a workshop—a paradigm shift for leaders who need to communicate, decide, and 
              inspire when data alone isn't enough.
            </p>
            
            {/* Audience badges */}
            <div className="flex flex-wrap gap-2 mb-10">
              {['CEOs & Founders', 'C-Suite', 'VPs & Directors', 'High-Potentials', 'Professionals', 'Students'].map((audience) => (
                <span key={audience} className="bg-white/10 text-gray-300 text-sm px-3 py-1 rounded-full border border-white/10">
                  {audience}
                </span>
              ))}
            </div>

            <div className="space-y-4 mb-10">
              {[
                'Structured thinking clarity in complex situations',
                'Powerful storytelling that inspires action',
                'Decision-making frameworks for uncertainty',
              ].map((item, index) => (
                <motion.div 
                  key={index} 
                  className="flex items-center gap-3"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-6 h-6 rounded-full bg-brand-gold/20 flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 text-brand-gold" />
                  </div>
                  <span className="text-gray-300">{item}</span>
                </motion.div>
              ))}
            </div>

            <Link
              to="/luminarc"
              className="inline-flex items-center gap-3 bg-brand-gold text-brand-charcoal font-semibold px-8 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
              data-testid="luminarc-cta"
            >
              <span>Discover Luminarc™</span>
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Right side - Visual element */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="hidden lg:block relative"
          >
            <div className="relative">
              {/* Glowing orb effect */}
              <div className="absolute inset-0 flex items-center justify-center">
                <motion.div
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 0.8, 0.5] }}
                  transition={{ duration: 4, repeat: Infinity }}
                  className="w-64 h-64 bg-brand-gold/20 rounded-full blur-3xl"
                />
              </div>
              
              {/* Testimonial card */}
              <div className="relative bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 mx-8">
                <Quote className="w-10 h-10 text-brand-gold/30 mb-4" />
                <p className="text-white text-lg italic mb-6">
                  "Luminarc™ transformed how I approach every strategic decision. 
                  The narrative thinking framework gave our entire leadership team a new lens."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-purple to-brand-gold flex items-center justify-center text-white font-bold">
                    ★
                  </div>
                  <div>
                    <p className="text-white font-semibold">Verified Client</p>
                    <p className="text-gray-400 text-sm">Executive Leader</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Impact Numbers Section
const ImpactSection = () => {
  const impacts = [
    { number: 500, suffix: '+', label: 'Leaders Transformed', description: 'Across industries and continents' },
    { number: 95, suffix: '%', label: 'Client Satisfaction', description: 'Would recommend us' },
    { number: 40, suffix: '%', label: 'Performance Improvement', description: 'Average client gain' },
    { number: 15, suffix: '+', label: 'Years Experience', description: 'Proven methodologies' },
  ];

  return (
    <section className="py-24 bg-brand-purple relative overflow-hidden" data-testid="impact-section">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(255,255,255,0.1),transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
            Our Impact in Numbers
          </h2>
          <p className="text-white/70 text-lg">
            Results that speak for themselves
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {impacts.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="text-center"
            >
              <p className="text-5xl md:text-6xl font-bold text-brand-gold mb-2">
                <AnimatedCounter end={item.number} suffix={item.suffix} />
              </p>
              <p className="text-white font-semibold mb-1">{item.label}</p>
              <p className="text-white/60 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us - Redesigned
const WhyChooseUs = () => {
  const differentiators = [
    {
      icon: Award,
      title: 'Battle-Tested Expertise',
      description: 'Our team has led transformation initiatives at Fortune 500 companies. We bring real-world experience, not just theory.',
    },
    {
      icon: Target,
      title: 'Results-Obsessed',
      description: 'We don\'t measure success by hours delivered. We measure it by outcomes achieved and capabilities built.',
    },
    {
      icon: Users,
      title: 'True Partnership',
      description: 'We work alongside your team, transfer knowledge, and build internal capabilities—not dependency on consultants.',
    },
    {
      icon: TrendingUp,
      title: 'Future-Ready',
      description: 'Our methodologies prepare leaders for tomorrow\'s challenges, including navigating AI disruption and complexity.',
    },
  ];

  return (
    <section className="py-24 bg-white" data-testid="why-choose-us">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block bg-brand-gold/10 text-brand-gold text-sm font-semibold px-4 py-2 rounded-full mb-4">
              Why Expertize Edge
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-brand-charcoal mb-8">
              Partners in Your{' '}
              <span className="text-brand-purple">Transformation</span>
            </h2>

            <div className="space-y-6">
              {differentiators.map((item, index) => (
                <motion.div 
                  key={index} 
                  className="flex gap-4 group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="w-14 h-14 bg-brand-lavender rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-brand-purple transition-colors duration-300">
                    <item.icon className="w-7 h-7 text-brand-purple group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-brand-charcoal text-lg mb-1">{item.title}</h3>
                    <p className="text-brand-muted">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Image with overlay card */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=700&fit=crop"
                alt="Business professionals collaborating"
                className="rounded-2xl shadow-2xl"
              />
              
              {/* Floating testimonial card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="absolute -bottom-8 -left-8 bg-white rounded-xl p-6 shadow-xl max-w-xs border border-brand-lavender"
              >
                <div className="flex items-center gap-1 mb-3">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <svg key={star} className="w-5 h-5 text-brand-gold" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-brand-body text-sm italic mb-3">
                  "The ROI on our engagement was evident within the first quarter."
                </p>
                <p className="text-brand-purple font-semibold text-sm">Verified Client</p>
                <p className="text-brand-muted text-xs">Senior Executive</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Banner
const CTABanner = () => {
  return (
    <section className="py-24 bg-gradient-to-br from-brand-charcoal to-brand-purple relative overflow-hidden" data-testid="cta-banner">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
          backgroundSize: '40px 40px',
        }} />
      </div>
      
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Build Your Competitive Edge?
          </h2>
          <p className="text-white/80 text-xl mb-10 max-w-2xl mx-auto">
            Let's discuss how we can help transform your organization. 
            Book a complimentary 30-minute discovery call.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-charcoal font-semibold px-10 py-4 hover:bg-brand-gold-lite hover:scale-105 transition-all duration-300 group"
              data-testid="cta-banner-button"
            >
              Book a Call
              <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold px-10 py-4 hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              Explore Services
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Main Home Page Component
const HomePage = () => {
  return (
    <main>
      <HeroSection />
      <ServicesOverview />
      <LuminarcSection />
      <ImpactSection />
      <WhyChooseUs />
      <CTABanner />
    </main>
  );
};

export default HomePage;
